import type { JSX } from 'react';

import { Button, Text } from '@change/ds-core-ui';
import { CheckIcon, LinkIcon } from '@change/ds-icons';
import { tv } from '@change/ds-utils';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { Translate } from '@change-corgi/core/react/i18n';

import type { ShareAction as ShareActionType, ShareChannel } from 'src/app/shared/hooks/share';

import { useCopyLinkVisibleUrlButton } from './hooks/useCopyLinkVisibleUrlButton';

type SupportedShareChannels = Exclude<ShareChannel, 'qrcode' | 'flyer'>;
type ShareButtonProps = Readonly<{
	buttonConfig: {
		ShareAction: ShareActionType<SupportedShareChannels>['ShareAction'];
		channel: SupportedShareChannels;
		label: TranslationKey;
		successLabel?: TranslationKey;
		url: string;
		urlDescription?: TranslationKey;
	};
}> &
	React.ComponentPropsWithoutRef<'div'>;

export function CopyLinkVisibleUrlButton({
	buttonConfig: { ShareAction, label, successLabel, url, urlDescription },
	...rest
}: ShareButtonProps): JSX.Element {
	const { data, actions } = useCopyLinkVisibleUrlButton({ label, successLabel, url });

	const buttonClass = tv({
		base: 'flex border-2 border-black',
		variants: {
			clicked: {
				true: 'text-typography-success',
				false: 'icon-strong-fixed',
			},
		},
	});

	return (
		<div className="flex flex-col gap-3 rounded-large border border-stroke-weaker px-5 py-4 elevation-low" {...rest}>
			<div className="rounded-lg bg-fill-weaker px-7 py-3 text-xs">
				<div className="leading-5 text-typography-strong">
					{urlDescription && <Translate value={urlDescription}></Translate>}
				</div>
				<div className="select-none overflow-hidden text-ellipsis whitespace-nowrap text-xs font-bold leading-6 text-typography-strong">
					{data.resolvedUrl}
				</div>
			</div>

			<ShareAction onAfterAction={actions.afterShare}>
				<Button
					leadingIcon={
						data.clickedState ? <CheckIcon data-qa="copied-icon-check" className="text-icon-success" /> : <LinkIcon />
					}
					variant="primary"
					disabled={data.clickedState}
					className={buttonClass({ clicked: data.clickedState })}
				>
					<Text size="medium" fontWeight="bold" className="flex gap-1.5" color="bg-red-500">
						<Translate value={data.buttonLabel} />
					</Text>
				</Button>
			</ShareAction>
		</div>
	);
}
